/**
 * Pixel-first analytics bridge.
 *
 * Instantiated once as `WYATT.Analytics` when this module loads.
 * The `<wyatt-analytics>` custom element resolves its payload and calls
 * `fromElement()` on connect, except `select_item` / `select_offer` which
 * publish on link click. Payload comes from a child `[data-payload]` JSON
 * script, or from a JS-assigned `.payload` property set before connect
 * (used by rewards offers). Events go through `Shopify.analytics.publish`.
 * Theme script tags sit before `content_for_header`, but this module is
 * deferred so Shopify is usually ready; `publish()` still queues until the
 * API exists. `publish()` is a no-op when `Shopify.designMode` or
 * `Shopify.inspectMode` is true (theme editor / inspector).
 *
 * Custom event names (values of `events`):
 * - `wyatt:page_context` — page metadata
 * - `wyatt:view_mini_cart` — mini cart open
 * - `wyatt:view_item_list` / `wyatt:select_item` — product grids
 * - `wyatt:select_nav_item` - click on a navigation link
 * - `wyatt:view_banner` / `wyatt:select_banner` - merchandising banners
 * - `wyatt:select_store` — preferred store selection
 * - `wyatt:view_offer_list` — `{ offers }` after rewards offers render
 * - `wyatt:select_offer` — full offer object on “See details” click
 * - `wyatt:submit_promo_code` — `{ cart, promo_code, rejected, reason }` after apply
 * - `wyatt:remove_promo_code` — `{ cart, promo_code }` after remove
 */

/**
 * @returns {((name: string, data: object) => void)|null}
 */
function getShopifyPublish() {
  return window.Shopify?.analytics?.publish ?? null;
}

/**
 * @param {string} name
 * @param {object} data
 */
function log(name, data) {
  if (window.Shopify?.theme?.role === "development") {
    // eslint-disable-next-line no-console -- dev-theme pixel debugging
    console.log("[WYATT.Analytics] publish", { name, data });
  }
}

class Analytics {
  events = {
    page_context: "wyatt:page_context",
    view_mini_cart: "wyatt:view_mini_cart",
    view_item_list: "wyatt:view_item_list",
    select_item: "wyatt:select_item",
    select_nav_item: "wyatt:select_nav_item",
    view_banner: "wyatt:view_banner",
    select_banner: "wyatt:select_banner",
    select_store: "wyatt:select_store",
    view_offer_list: "wyatt:view_offer_list",
    select_offer: "wyatt:select_offer",
    submit_promo_code: "wyatt:submit_promo_code",
    remove_promo_code: "wyatt:remove_promo_code",
  };

  /** @type {[string, object][]} */
  #pendingPublishes = [];

  /**
   * Publish from a `<wyatt-analytics data-event="…">` element using its
   * already-parsed `payload` property. `data-event` matches `events` keys
   * (snake_case).
   *
   * @param {Element & { payload?: object }} el
   */
  fromElement(el) {
    const event = el?.dataset?.event;

    if (!event || el.payload == null) {
      return;
    }

    this.publish(this.events[event], el.payload);
  }

  /**
   * @param {string} name
   * @param {object} [data]
   */
  publish(name, data = {}) {
    if (window.Shopify?.designMode || window.Shopify?.inspectMode) {
      return;
    }

    try {
      this.#publishPending();

      const shopifyPublish = getShopifyPublish();

      if (!shopifyPublish) {
        this.#pendingPublishes.push([name, data]);
        return;
      }

      log(name, data);
      shopifyPublish(name, data);
    } catch {
      // noop
    }
  }

  #publishPending() {
    const shopifyPublish = getShopifyPublish();

    if (!shopifyPublish || this.#pendingPublishes.length === 0) {
      return;
    }

    while (this.#pendingPublishes.length > 0) {
      const [name, data] = this.#pendingPublishes.shift();

      try {
        log(name, data);
        shopifyPublish(name, data);
      } catch {
        // noop
      }
    }
  }
}

WYATT.Analytics = new Analytics();

export default Analytics;
